import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface LeadCallLog {
  id: string;
  lead_id: string;
  call_date: string;
  transcript: string | null;
  next_steps: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  audio_path: string | null;
  audio_duration_seconds: number | null;
  transcribed_at: string | null;
}

export interface CreateCallLogData {
  lead_id: string;
  call_date: string;
  transcript?: string;
  next_steps?: string;
  audio_path?: string | null;
  audio_duration_seconds?: number | null;
  transcribed_at?: string | null;
}

export function useLeadCallLogs(leadId?: string) {
  const [callLogs, setCallLogs] = useState<LeadCallLog[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchCallLogs = useCallback(async (id?: string) => {
    const targetLeadId = id || leadId;
    if (!targetLeadId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('lead_call_logs')
        .select('*')
        .eq('lead_id', targetLeadId)
        .order('call_date', { ascending: false });

      if (error) throw error;
      setCallLogs((data || []) as LeadCallLog[]);
    } catch (error) {
      console.error('Error fetching call logs:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger le journal d'appels",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [leadId, toast]);

  const addCallLog = async (data: CreateCallLogData) => {
    try {
      const { data: user } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('lead_call_logs')
        .insert({
          lead_id: data.lead_id,
          call_date: data.call_date,
          transcript: data.transcript || null,
          next_steps: data.next_steps || null,
          audio_path: data.audio_path ?? null,
          audio_duration_seconds: data.audio_duration_seconds ?? null,
          transcribed_at: data.transcribed_at ?? null,
          created_by: user?.user?.id || null,
        });

      if (error) throw error;

      toast({
        title: "Appel enregistré",
        description: "Le compte-rendu d'appel a été sauvegardé",
      });

      await fetchCallLogs(data.lead_id);
      return true;
    } catch (error) {
      console.error('Error adding call log:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer l'appel",
        variant: "destructive",
      });
      return false;
    }
  };

  const updateCallLog = async (logId: string, updates: Partial<LeadCallLog>) => {
    try {
      const payload: Record<string, unknown> = {
        call_date: updates.call_date,
        transcript: updates.transcript,
        next_steps: updates.next_steps,
      };
      if (updates.audio_path !== undefined) payload.audio_path = updates.audio_path;
      if (updates.audio_duration_seconds !== undefined) payload.audio_duration_seconds = updates.audio_duration_seconds;
      if (updates.transcribed_at !== undefined) payload.transcribed_at = updates.transcribed_at;

      const { error } = await supabase
        .from('lead_call_logs')
        .update(payload)
        .eq('id', logId);

      if (error) throw error;

      toast({
        title: "Appel modifié",
        description: "Le compte-rendu a été mis à jour",
      });

      await fetchCallLogs();
      return true;
    } catch (error) {
      console.error('Error updating call log:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier l'appel",
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteCallLog = async (logId: string) => {
    try {
      // Best-effort cleanup of any audio file
      const log = callLogs.find(l => l.id === logId);
      if (log?.audio_path) {
        await supabase.storage.from('call-recordings').remove([log.audio_path]).catch(() => {});
      }

      const { error } = await supabase
        .from('lead_call_logs')
        .delete()
        .eq('id', logId);

      if (error) throw error;

      toast({
        title: "Appel supprimé",
        description: "Le compte-rendu a été supprimé",
      });

      await fetchCallLogs();
      return true;
    } catch (error) {
      console.error('Error deleting call log:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'appel",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    callLogs,
    loading,
    fetchCallLogs,
    addCallLog,
    updateCallLog,
    deleteCallLog,
  };
}

// Hook to fetch call log counts for multiple leads
export function useLeadCallLogCounts() {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);

  const fetchCounts = useCallback(async (leadIds: string[]) => {
    if (leadIds.length === 0) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('lead_call_logs')
        .select('lead_id')
        .in('lead_id', leadIds);

      if (error) throw error;

      // Count occurrences per lead
      const countMap: Record<string, number> = {};
      (data || []).forEach(row => {
        countMap[row.lead_id] = (countMap[row.lead_id] || 0) + 1;
      });
      setCounts(countMap);
    } catch (error) {
      console.error('Error fetching call log counts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  return { counts, loading, fetchCounts };
}
